import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import './index.css';
import App from './App';

// ---- keep the login token out of request addresses ----
// Pages build requests like `${API}/novels/1?token=...`. Addresses end up in the
// server host's logs, so before any request to our own server goes out, the
// token is taken out of the address and sent in a header instead. The server
// reads it from there. Pages don't need to change.
const API = process.env.REACT_APP_BACKEND_URL || '';

const pullTokenFromUrl = (url) => {
  const match = url.match(/[?&]token=([^&#]*)/);
  if (!match) return { url, token: null };
  let token = match[1];
  try { token = decodeURIComponent(token); } catch (e) { /* use as-is */ }
  const cleaned = url
    // "?token=X&next" -> "?next", "&token=X" -> ""
    .replace(/([?&])token=[^&#]*(&)?/, (all, sep, amp) => (amp ? sep : ''))
    .replace(/[?&](#|$)/, '$1');
  return { url: cleaned, token };
};

axios.interceptors.request.use((config) => {
  const url = config.url || '';
  // only our own server gets the token — never Cloudinary or anything else
  if (!API || !url.startsWith(API)) return config;

  const pulled = pullTokenFromUrl(url);
  let token = pulled.token;
  config.url = pulled.url;

  if (config.params && Object.prototype.hasOwnProperty.call(config.params, 'token')) {
    const { token: fromParams, ...rest } = config.params;
    token = token || fromParams;
    config.params = rest;
  }
  if (!token) {
    try { token = localStorage.getItem('token'); } catch (e) { token = null; }
  }
  if (token) {
    if (config.headers && typeof config.headers.set === 'function') {
      config.headers.set('Authorization', `Bearer ${token}`);
    } else {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
    }
  }
  return config;
});

// ---- signed out elsewhere? go to the login page ----
// If this device's login stops working (signed out from Settings, password
// changed on another device, or it expired), the server answers 401. Clear
// the old login and go to the login page instead of showing empty screens.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error?.config?.url || '';
    const isOurs = API && url.startsWith(API);
    const isAuthRoute = /\/auth\//.test(url) || /\/visit(\?|$)/.test(url);
    if (error?.response?.status === 401 && isOurs && !isAuthRoute
        && !window.location.pathname.startsWith('/login')) {
      try { localStorage.removeItem('token'); } catch (e) { /* ignore */ }
      window.location.assign('/login');
    }
    return Promise.reject(error);
  }
);

// ---- note each visit for the Security page ----
// One small request per time the site is opened: which page it landed on.
// The server records the visitor's address and device from the request itself.
if (API) {
  axios.post(`${API}/visit`, { path: window.location.pathname }).catch(() => {});
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
